import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ResizableImage } from './tiptap/extensions/ResizableImage';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import Blockquote from '@tiptap/extension-blockquote';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import HardBreak from '@tiptap/extension-hard-break';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import FontFamily from '@tiptap/extension-font-family';
import { FontSize } from './tiptap/extensions/FontSize';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import CodeBlock from '@tiptap/extension-code-block';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { toast } from 'react-toastify';
import { uploadImageToGetMediaFileUrl } from '../utils/API_SERVICE';
import { DraggableParagraph } from './tiptap/extensions/DraggableParagraph';
import { DraggableListItem } from './tiptap/extensions/DraggableListItem';
import { HeadingWithClass } from './tiptap/extensions/HeadingWithClass';
import { OrderedListWithClass } from './tiptap/extensions/OrderedListWithClass';
import { BulletListWithClass } from './tiptap/extensions/BulletListWithClass';
import { NormalizeOrderedLists } from './tiptap/extensions/NormalizeOrderedLists';
import { AIDiffInline } from './tiptap/extensions/AIDiffInline';
import { FormulaPendingChip } from './tiptap/extensions/FormulaPendingChip';
import AutoLangSpellcheck from './tiptap/extensions/AutoLangSpellcheck';
import RTEToolbar from './tiptap/RTEToolbar';
import ImageAnnotationModal from './tiptap/ImageAnnotationModal';
import { dropPoint } from 'prosemirror-transform';
import { NodeSelection, TextSelection } from 'prosemirror-state';
import { MEDILIS_DRAG_SCROLL_MESSAGE_TYPE, MEDILIS_VISIBLE_VIEWPORT_MESSAGE_TYPE } from '../pages/patient/hooks/cre/creMirrorConstants';
import { prepareReportHtmlForLanguage } from '../utils/normalizeRtlReportHtml';
import { applySanitizedSelectionClipboard } from '../utils/reportClipboard';
import './tiptap/RTEStyles.css';

/** Edge band (px) against the *visible* clip of the editor (not off-screen box edges). */
const DRAG_SCROLL_EDGE_PX = 56;
/** Pixels per animation frame while auto-scrolling during drag. */
const DRAG_SCROLL_STEP_PX = 18;
/** Min interval between parent-scroll requests (avoids flooding the host). */
const PARENT_SCROLL_MIN_INTERVAL_MS = 50;

let dragScrollRaf = null;
let dragScrollDir = 0;
/** @type {Element[]} */
let dragScrollTargets = [];
let lastParentScrollAt = 0;

/**
 * Host-reported visible band of this iframe, in iframe viewport coordinates.
 * When the iframe is taller than the host clip (e.g. 1188px iframe, ~500px
 * visible), `window.innerHeight` is wrong for edge detection — use this instead.
 * @type {{ top: number, bottom: number } | null}
 */
let parentVisibleViewport = null;
let parentViewportListenerBound = false;

function ensureParentVisibleViewportListener() {
  if (parentViewportListenerBound || typeof window === 'undefined') return;
  parentViewportListenerBound = true;
  window.addEventListener('message', (event) => {
    const data = event?.data;
    if (!data || data.type !== MEDILIS_VISIBLE_VIEWPORT_MESSAGE_TYPE) return;
    const top = Number(data.top);
    const bottom = Number(data.bottom);
    if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom <= top) {
      parentVisibleViewport = null;
      return;
    }
    parentVisibleViewport = { top, bottom };
  });
}

function clearDragAutoScroll() {
  dragScrollDir = 0;
  dragScrollTargets = [];
  if (dragScrollRaf != null) {
    cancelAnimationFrame(dragScrollRaf);
    dragScrollRaf = null;
  }
}

function isVerticallyScrollable(el) {
  if (!el || el === document.body) return false;
  if (!(el instanceof HTMLElement) && el !== document.documentElement) {
    return false;
  }
  const style = window.getComputedStyle(el);
  const oy = style.overflowY;
  if (oy !== 'auto' && oy !== 'scroll' && oy !== 'overlay') return false;
  return el.scrollHeight > el.clientHeight + 1;
}

function canScrollInDir(el, dir) {
  if (!el || !dir) return false;
  if (dir < 0) return el.scrollTop > 0;
  return el.scrollTop + el.clientHeight < el.scrollHeight - 1;
}

/**
 * Visible portion of `el` after clipping to the viewport, overflow ancestors,
 * and (when provided) the host-reported iframe visible band.
 */
function getVisibleClipRect(el) {
  ensureParentVisibleViewportListener();

  const viewportBottom =
    typeof window !== 'undefined' ? window.innerHeight : 0;
  let top = 0;
  let bottom = viewportBottom;

  // Host clip wins over full iframe height when the iframe is partially shown.
  if (parentVisibleViewport) {
    top = Math.max(top, parentVisibleViewport.top);
    bottom = Math.min(bottom, parentVisibleViewport.bottom);
  }

  const rect = el.getBoundingClientRect();
  top = Math.max(top, rect.top);
  bottom = Math.min(bottom, rect.bottom);

  let node = el.parentElement;
  while (node && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const oy = style.overflowY;
    const ox = style.overflowX;
    if (
      /(auto|scroll|overlay|hidden)/.test(oy) ||
      /(auto|scroll|overlay|hidden)/.test(ox)
    ) {
      const r = node.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
    }
    node = node.parentElement;
  }

  return { top, bottom, height: bottom - top };
}

function collectScrollTargets(innerEl) {
  const targets = [];
  if (innerEl) targets.push(innerEl);
  let node = innerEl?.parentElement;
  while (node && node !== document.documentElement) {
    if (isVerticallyScrollable(node)) targets.push(node);
    node = node.parentElement;
  }
  const root = document.scrollingElement || document.documentElement;
  if (root && !targets.includes(root)) targets.push(root);
  return targets;
}

/**
 * When the iframe document cannot scroll further, ask the host to scroll.
 * Same-origin parents are scrolled directly; cross-origin hosts get postMessage.
 */
function requestParentDragScroll(dir, step) {
  if (typeof window === 'undefined' || !window.parent || window.parent === window) {
    return;
  }
  const now = Date.now();
  if (now - lastParentScrollAt < PARENT_SCROLL_MIN_INTERVAL_MS) return;
  lastParentScrollAt = now;

  // Same-origin host: scroll its document (and common scroll containers).
  try {
    const parentWin = window.parent;
    const parentDoc = parentWin.document;
    const candidates = [];
    const root = parentDoc.scrollingElement || parentDoc.documentElement;
    if (root) candidates.push(root);
    if (parentDoc.body && parentDoc.body !== root) candidates.push(parentDoc.body);
    // Prefer the element that currently wraps our iframe, if scrollable.
    try {
      const frameEl =
        parentWin.frameElement ||
        Array.from(parentDoc.querySelectorAll('iframe')).find(
          (f) => f.contentWindow === window,
        );
      let node = frameEl?.parentElement;
      while (node && node !== parentDoc.documentElement) {
        if (isVerticallyScrollable(node)) candidates.unshift(node);
        node = node.parentElement;
      }
    } catch (_) {
      /* ignore frameElement lookup failures */
    }
    for (let i = 0; i < candidates.length; i += 1) {
      const el = candidates[i];
      if (!canScrollInDir(el, dir)) continue;
      el.scrollTop += dir * step;
      return;
    }
  } catch (_) {
    // Cross-origin: fall through to postMessage.
  }

  try {
    window.parent.postMessage(
      {
        type: MEDILIS_DRAG_SCROLL_MESSAGE_TYPE,
        direction: dir,
        step,
      },
      '*',
    );
  } catch (_) {
    /* parent may be inaccessible */
  }
}

function tickDragAutoScroll() {
  if (!dragScrollDir || dragScrollTargets.length === 0) {
    dragScrollRaf = null;
    return;
  }
  // Inner editor first, then outer page/section scrollers inside the iframe.
  let scrolledLocal = false;
  for (let i = 0; i < dragScrollTargets.length; i += 1) {
    const el = dragScrollTargets[i];
    if (!canScrollInDir(el, dragScrollDir)) continue;
    el.scrollTop += dragScrollDir * DRAG_SCROLL_STEP_PX;
    scrolledLocal = true;
    break;
  }
  // Iframe at scroll limit → ask parent (Radical) to scroll its page.
  if (!scrolledLocal) {
    requestParentDragScroll(dragScrollDir, DRAG_SCROLL_STEP_PX);
  }
  dragScrollRaf = requestAnimationFrame(tickDragAutoScroll);
}

/**
 * Auto-scroll while dragging near the *visible* top/bottom of the editor.
 * Uses viewport/ancestor clipping so it still works when the editor's own
 * box edges are off-screen (tall editor + outer page scroll).
 */
function updateDragAutoScroll(view, clientY) {
  const inner =
    view?.dom && typeof view.dom.closest === 'function'
      ? view.dom.closest('.rte-editor-scroll')
      : null;
  if (!inner || typeof clientY !== 'number') {
    clearDragAutoScroll();
    return;
  }

  const clip = getVisibleClipRect(inner);
  if (clip.height < 24) {
    clearDragAutoScroll();
    return;
  }

  let dir = 0;
  if (clientY < clip.top + DRAG_SCROLL_EDGE_PX) dir = -1;
  else if (clientY > clip.bottom - DRAG_SCROLL_EDGE_PX) dir = 1;

  if (dir === 0) {
    clearDragAutoScroll();
    return;
  }

  dragScrollDir = dir;
  dragScrollTargets = collectScrollTargets(inner);
  if (dragScrollRaf == null) {
    dragScrollRaf = requestAnimationFrame(tickDragAutoScroll);
  }
}

/**
 * Production-ready Tiptap-based Rich Text Editor
 */
function RTE({
  name,
  label,
  defaultValue = '',
  value: controlledValue,
  heightValue = 400,
  onChange,
  onFocus,
  onBlur,
  editorRef,
  language = 'en',
}) {
  const [value, setValue] = useState(defaultValue);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.body.classList.contains('dark');
  });
  const [imageToAnnotate, setImageToAnnotate] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const scrollInfoRef = useRef(null);
  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;

  const notifyEditorSurfaceFocus = useCallback(() => {
    onFocusRef.current?.();
  }, []);

  // Upload function for API
  const uploadImageToAPI = useCallback(async (file) => {
    try {
      const imageUrl = await uploadImageToGetMediaFileUrl(file);
      return imageUrl;
    } catch (error) {
      console.error('API upload error:', error);
      throw error;
    }
  }, []);

  // Dark mode observer
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDarkMode(document.body.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    observer.observe(document.body, { attributes: true });

    return () => observer.disconnect();
  }, []);

  // Sync controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setValue(controlledValue);
    } else {
      setValue(defaultValue);
    }
  }, [defaultValue, controlledValue]);

  const editor = useEditor({
    extensions: [
      // Use default StarterKit but exclude extensions we're adding separately with custom configs
      // Note: StarterKit includes Document, Paragraph, Text, History, Gapcursor, Dropcursor, etc.
      StarterKit.configure({
        paragraph: false, // Exclude default paragraph, use DraggableParagraph instead
        heading: false, // Exclude default heading, use HeadingWithClass so class is preserved
        listItem: false, // Exclude default listItem, use DraggableListItem instead
        orderedList: false, // Exclude default; use OrderedListWithClass so AI diff class is preserved
        bulletList: false, // Exclude default; use BulletListWithClass so AI diff class is preserved
        strike: false, // Exclude default strike, use custom configured version
        code: false, // Exclude default code, use custom configured version
        codeBlock: false, // Exclude default codeBlock, use custom configured version
        blockquote: false, // Exclude default blockquote, use custom configured version
        horizontalRule: false, // Exclude default horizontalRule, use custom configured version
        hardBreak: false, // Exclude default hardBreak, use custom extended version
      }),
      // Extend default listItem to add drag functionality
      DraggableListItem,
      OrderedListWithClass,
      BulletListWithClass,
      NormalizeOrderedLists,
      // Extend default paragraph to add drag functionality
      DraggableParagraph,
      HeadingWithClass,
      ResizableImage.configure({
        inline: false,
        allowBase64: false,
      }),
      // Custom Link extension - make it non-inclusive so users can exit links
      Link.extend({
        inclusive: false,
      }).configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 dark:text-blue-400 underline',
        },
      }),
      Placeholder.configure({
        placeholder: 'Start dictating your findings here...',
      }),
      TextStyle,
      FontFamily.configure({
        types: ['textStyle'],
      }),
      FontSize.configure({
        types: ['textStyle'],
      }),
      Color,
      AIDiffInline, // Preserve htmlDiff inline spans so highlights are not dropped on setContent()
      FormulaPendingChip, // ProcessWithAI needs_input pending chips (data-formula-pending)
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph', 'image'],
      }),
      Underline,
      Strike,
      Code.configure({
        HTMLAttributes: {
          class: 'bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded',
        },
      }),
      CodeBlock.configure({
        HTMLAttributes: {
          class: 'bg-gray-100 dark:bg-gray-800 p-4 rounded-lg font-mono text-sm',
        },
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Blockquote.configure({
        HTMLAttributes: {
          class: 'border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic',
        },
      }),
      HorizontalRule,
      HardBreak.extend({
        addKeyboardShortcuts() {
          return {};
        },
      }),
      // Make subscript and superscript mutually exclusive
      Subscript.extend({
        excludes: 'superscript',
      }),
      Superscript.extend({
        excludes: 'subscript',
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse',
        },
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'border',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border bg-gray-50 dark:bg-gray-700 font-semibold',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border',
        },
      }),
      AutoLangSpellcheck,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setValue(html);
      if (onChange) {
        onChange(html);
      }
    },
    onFocus: () => {
      notifyEditorSurfaceFocus();
    },
    onSelectionUpdate: ({ editor: activeEditor }) => {
      if (activeEditor.isFocused) {
        notifyEditorSurfaceFocus();
      }
    },
    onBlur: () => {
      if (onBlur) onBlur();
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none tiptap-editor rte-drop-target report-no-hyphens ${
          isDarkMode ? 'dark:prose-invert' : ''
        } ${language === 'ar' || language === 'he' ? 'rtl' : 'ltr'}`,
        style: `min-height: ${heightValue}px; padding: 12px; width: 100%; min-width: 100%;`,
        dir: language === 'ar' || language === 'he' ? 'rtl' : 'ltr',
        spellcheck: 'true',
        // Base lang is a fallback; blocks get their own lang/dir via AutoLangSpellcheck.
        lang: language === 'he' || language === 'ar' ? 'he' : 'en',
      },
      handleDOMEvents: {
        mousedown: () => {
          notifyEditorSurfaceFocus();
          return false;
        },
        // Own plain-text clipboard so bidi marks / typographic dashes don't
        // become '#' when pasted into Notepad or other ANSI/legacy apps.
        copy: (_view, event) => {
          return applySanitizedSelectionClipboard(event);
        },
        cut: (view, event) => {
          if (!applySanitizedSelectionClipboard(event)) return false;
          // We prevented default; delete the selection ourselves.
          const { state, dispatch } = view;
          if (!state.selection.empty) {
            dispatch(state.tr.deleteSelection().scrollIntoView());
          }
          return true;
        },
        dragover: (view, event) => {
          updateDragAutoScroll(view, event.clientY);
          return false;
        },
        drop: () => {
          clearDragAutoScroll();
          return false;
        },
        dragend: () => {
          clearDragAutoScroll();
          return false;
        },
        dragleave: (view, event) => {
          const related = event.relatedTarget;
          if (
            related &&
            view?.dom &&
            typeof view.dom.contains === 'function' &&
            view.dom.contains(related)
          ) {
            return false;
          }
          clearDragAutoScroll();
          return false;
        },
      },
      handleDrop: (view, event, slice, moved) => {
        clearDragAutoScroll();
        // Image drop from outside
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            handleImageUpload(file);
            return true;
          }
        }

        // List items: drop into the target ol/ul by index. Default PM drop
        // often inserts a *new* sibling <ol> when the pointer is above the
        // first item (schema can't place a bare listItem at doc level),
        // which breaks Impression numbering (1, 1, 2, 3…).
        const sliceRoot = slice?.content?.firstChild;
        const isListItemSlice =
          slice &&
          slice.content.childCount === 1 &&
          (sliceRoot?.type.name === 'listItem' ||
            ((sliceRoot?.type.name === 'orderedList' ||
              sliceRoot?.type.name === 'bulletList') &&
              sliceRoot.childCount === 1 &&
              sliceRoot.firstChild?.type.name === 'listItem'));
        if (moved && isListItemSlice) {
          const listItemNode =
            sliceRoot.type.name === 'listItem'
              ? sliceRoot
              : sliceRoot.firstChild;
          const rect = view.dom.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const coords = view.posAtCoords({
            left: centerX,
            top: event.clientY,
          });
          if (coords != null) {
            const $mouse = view.state.doc.resolve(coords.pos);
            let listDepth = -1;
            for (let d = $mouse.depth; d > 0; d -= 1) {
              const name = $mouse.node(d).type.name;
              if (name === 'orderedList' || name === 'bulletList') {
                listDepth = d;
                break;
              }
            }

            // Dropped just above/below a list (e.g. on IMPRESSION heading gap):
            // use the nearest following/preceding list of a compatible type.
            let listPos = -1;
            let insertIndex = 0;
            if (listDepth > 0) {
              listPos = $mouse.before(listDepth);
              insertIndex = $mouse.index(listDepth);
              // Prefer inserting before the item under the cursor when the
              // pointer is in the top half of that item.
              try {
                const itemPos = $mouse.before(listDepth + 1);
                const itemDom = view.nodeDOM(itemPos);
                if (itemDom && itemDom.getBoundingClientRect) {
                  const itemRect = itemDom.getBoundingClientRect();
                  if (event.clientY < itemRect.top + itemRect.height / 2) {
                    insertIndex = $mouse.index(listDepth);
                  } else {
                    insertIndex = $mouse.index(listDepth) + 1;
                  }
                }
              } catch (_) {
                /* keep insertIndex from resolve */
              }
            } else {
              const doc = view.state.doc;
              let best = null;
              doc.descendants((node, pos) => {
                if (
                  node.type.name !== 'orderedList' &&
                  node.type.name !== 'bulletList'
                ) {
                  return;
                }
                const dom = view.nodeDOM(pos);
                if (!dom || !dom.getBoundingClientRect) return;
                const r = dom.getBoundingClientRect();
                const dist =
                  event.clientY < r.top
                    ? r.top - event.clientY
                    : event.clientY > r.bottom
                      ? event.clientY - r.bottom
                      : 0;
                if (best == null || dist < best.dist) {
                  best = { pos, dist, top: r.top, bottom: r.bottom };
                }
              });
              if (best && best.dist < 80) {
                listPos = best.pos;
                insertIndex =
                  event.clientY < best.top + (best.bottom - best.top) / 2
                    ? 0
                    : view.state.doc.nodeAt(best.pos)?.childCount || 0;
              }
            }

            if (listPos >= 0) {
              const tr = view.state.tr;
              const { from, to } = view.state.selection;
              // Delete dragged node first, then map the list position.
              tr.delete(from, to);
              const mappedListPos = tr.mapping.map(listPos);
              const listNode = tr.doc.nodeAt(mappedListPos);
              if (
                listNode &&
                (listNode.type.name === 'orderedList' ||
                  listNode.type.name === 'bulletList')
              ) {
                // Adjust insert index when the deleted item was in the same
                // list and before the drop index.
                let index = insertIndex;
                const $selFrom = view.state.doc.resolve(from);
                let selListDepth = -1;
                for (let d = $selFrom.depth; d > 0; d -= 1) {
                  if (
                    $selFrom.node(d).type.name === 'orderedList' ||
                    $selFrom.node(d).type.name === 'bulletList'
                  ) {
                    selListDepth = d;
                    break;
                  }
                }
                if (
                  selListDepth > 0 &&
                  $selFrom.before(selListDepth) === listPos
                ) {
                  const deletedIndex = $selFrom.index(selListDepth);
                  if (deletedIndex < index) index -= 1;
                }
                index = Math.max(0, Math.min(index, listNode.childCount));

                let insertAt = mappedListPos + 1;
                for (let i = 0; i < index; i += 1) {
                  insertAt += listNode.child(i).nodeSize;
                }
                tr.insert(insertAt, listItemNode);
                tr.setSelection(NodeSelection.create(tr.doc, insertAt));
                view.focus();
                view.dispatch(tr.setMeta('uiEvent', 'drop'));
                event.preventDefault();
                return true;
              }
            }
          }
          // Fall through to PM default if we couldn't resolve a target list.
          return false;
        }

        // Reliable drop position for internal paragraph/heading drags:
        // use vertical center of editor so the X axis doesn't jitter.
        if (moved && slice && slice.size) {
          const rect = view.dom.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const eventPos = view.posAtCoords({ left: centerX, top: event.clientY });
          if (eventPos != null) {
            const $mouse = view.state.doc.resolve(eventPos.pos);
            let insertPos = dropPoint(view.state.doc, $mouse.pos, slice);
            if (insertPos == null) insertPos = $mouse.pos;

            const tr = view.state.tr;
            tr.deleteSelection();
            const pos = tr.mapping.map(insertPos);
            const isNode = slice.openStart === 0 && slice.openEnd === 0 && slice.content.childCount === 1;
            const beforeInsert = tr.doc;
            if (isNode) {
              tr.replaceRangeWith(pos, pos, slice.content.firstChild);
            } else {
              tr.replaceRange(pos, pos, slice);
            }
            if (!tr.doc.eq(beforeInsert)) {
              const $pos = tr.doc.resolve(pos);
              if (isNode && NodeSelection.isSelectable(slice.content.firstChild) && $pos.nodeAfter && $pos.nodeAfter.sameMarkup(slice.content.firstChild)) {
                tr.setSelection(NodeSelection.create(tr.doc, pos));
              } else {
                let end = tr.mapping.map(insertPos);
                tr.mapping.maps[tr.mapping.maps.length - 1].forEach((_from, _to, _newFrom, newTo) => { end = newTo; });
                tr.setSelection(TextSelection.between(tr.doc.resolve(pos), tr.doc.resolve(end)));
              }
              view.focus();
              view.dispatch(tr.setMeta('uiEvent', 'drop'));
              event.preventDefault();
              return true;
            }
          }
        }
        return false;
      },
      handlePaste: (view, event, slice) => {
        const items = Array.from(event.clipboardData?.items || []);
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              handleImageUpload(file);
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  // Handle image upload with annotation option
  const handleImageUpload = useCallback(async (file) => {
    // Show annotation modal first
    setImageToAnnotate(file);
  }, []);

  // Handle annotated image save
  const handleAnnotatedImageSave = useCallback(async (annotatedFile) => {
    try {
      setIsUploadingImage(true);
      const imageUrl = await uploadImageToAPI(annotatedFile);
      if (editor && imageUrl) {
        // Add cache-busting parameter to prevent showing old cached images
        const cacheBuster = `?t=${Date.now()}`;
        const imageUrlWithCacheBuster = imageUrl.includes('?') 
          ? `${imageUrl}&t=${Date.now()}` 
          : `${imageUrl}${cacheBuster}`;
        
        // Get current text alignment from selection or active state
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;
        
        // Find parent paragraph or heading to get alignment
        let currentAlignment = 'left';
        for (let depth = $from.depth; depth > 0; depth--) {
          const parent = $from.node(depth);
          if (parent.type.name === 'paragraph' || parent.type.name === 'heading') {
            currentAlignment = parent.attrs.textAlign || 'left';
            break;
          }
        }
        
        // Insert image as block with alignment attribute
        editor.chain().focus().setImage({ 
          src: imageUrlWithCacheBuster,
          align: currentAlignment,
        }).run();
        
        toast.success('Image uploaded successfully!');
      }
      setImageToAnnotate(null);
      setIsUploadingImage(false);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image. Please try again.');
      setImageToAnnotate(null);
      setIsUploadingImage(false);
    }
  }, [editor, uploadImageToAPI]);

  // Handle annotation cancel
  const handleAnnotationCancel = useCallback(() => {
    setImageToAnnotate(null);
  }, []);

  // Update editor content when value changes externally
  useEffect(() => {
    if (!editor) return;
    const nextHtml = prepareReportHtmlForLanguage(value || "", language);
    if (nextHtml === editor.getHTML()) return;
    queueMicrotask(() => {
      if (!editor || nextHtml === editor.getHTML()) return;
      try {
        editor.commands.setContent(nextHtml, false);
      } catch (err) {
        // Position-outside-fragment can happen when selection is stale after doc change; reset then setContent
        if (err?.message?.includes('outside of fragment') || err?.message?.includes('Invalid position')) {
          try {
            editor.chain().setTextSelection(0).setContent(nextHtml, false).run();
          } catch (_) {
            editor.commands.setContent(nextHtml, false);
          }
        } else {
          throw err;
        }
      }
    });
  }, [value, editor, language]);

  // Clear stale drag state when doc changes so ProseMirror's updateDraggedNode doesn't throw "Position outside of fragment"
  useEffect(() => {
    if (!editor?.view) return;
    const view = editor.view;
    const originalDispatch = view.dispatch.bind(view);
    view.dispatch = function (tr) {
      if (tr.docChanged && view.dragging) view.dragging = null;
      return originalDispatch(tr);
    };
    return () => {
      view.dispatch = originalDispatch;
    };
  }, [editor]);

  // Set RTL direction
  useEffect(() => {
    if (editor) {
      const isRTL = language === 'ar' || language === 'he';
      editor.view.dom.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    }
  }, [editor, language]);

  // Create API wrapper for editor ref
  useEffect(() => {
    if (editorRef && editor) {
      // Get the actual content editable element (ProseMirror editor)
      const wysiwygElement = editor.view.dom.querySelector('.ProseMirror') || editor.view.dom;

      // Create API wrapper for compatibility
      editorRef.current = {
        editor: {
          // Expose Tiptap commands directly for voice commands
          commands: editor.commands,
          
          // Expose chain() method for Tiptap commands
          chain: () => editor.chain(),

          // Expose can() for undo/redo and other checks
          can: () => editor.can(),

          // TipTap event API (update / transaction) for hooks that listen to edits,
          // e.g. useCreReportHtmlSync and useRadicalPeerReviewMessage.
          on: (event, handler) => editor.on(event, handler),
          off: (event, handler) => editor.off(event, handler),
          
          // Core methods - used by hooks
          getContents: () => editor.getHTML(),
          getHTML: () => editor.getHTML(), // Alias for Tiptap compatibility
          setContents: (content) => {
            const nextHtml = prepareReportHtmlForLanguage(content || "", language);
            const currentContent = editor.getHTML();
            if (nextHtml !== currentContent) {
              queueMicrotask(() => {
                if (!editor || nextHtml === editor.getHTML()) return;
                try {
                  editor.commands.setContent(nextHtml, false);
                } catch (err) {
                  if (err?.message?.includes('outside of fragment') || err?.message?.includes('Invalid position')) {
                    try {
                      editor.chain().setTextSelection(0).setContent(nextHtml, false).run();
                    } catch (_) {}
                  }
                }
              });
            }
          },
          insertHTML: (html) => {
            editor.chain().focus().insertContent(html).run();
          },
          
          // Expose Tiptap state for advanced operations
          get state() {
            return editor.state;
          },

          // ProseMirror view - needed for scroll-into-view and DOM position APIs
          get view() {
            return editor.view;
          },
          
          // Core API for cursor/selection management - critical for voice commands
          core: {
            // Get current selection range - used extensively by useRteWithSpeech
            getRange: () => {
              try {
                const { state } = editor.view;
                const { selection } = state;
                const { $from, $to } = selection;
                
                // Convert Tiptap positions to DOM range
                const startPos = editor.view.domAtPos($from.pos);
                const endPos = editor.view.domAtPos($to.pos);
                
                const domRange = document.createRange();
                domRange.setStart(startPos.node, startPos.offset);
                domRange.setEnd(endPos.node, endPos.offset);
                return domRange;
              } catch (e) {
                // Fallback: use browser selection
                const sel = window.getSelection();
                if (sel && sel.rangeCount > 0) {
                  return sel.getRangeAt(0).cloneRange();
                }
                // Create empty range at end of editor
                const range = document.createRange();
                range.selectNodeContents(wysiwygElement);
                range.collapse(false);
                return range;
              }
            },
            
            // Set selection range - used by voice commands and cursor management
            setRange: (startContainer, startOffset, endContainer, endOffset) => {
              try {
                const range = document.createRange();
                range.setStart(startContainer, startOffset);
                if (endContainer && endOffset !== undefined) {
                  range.setEnd(endContainer, endOffset);
                } else {
                  range.setEnd(startContainer, startOffset);
                }
                
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
                
                // Sync with Tiptap selection
                try {
                  const startPos = editor.view.posAtDOM(startContainer, startOffset);
                  const endPos = endContainer 
                    ? editor.view.posAtDOM(endContainer, endOffset)
                    : startPos;
                  
                  editor.commands.setTextSelection({ from: startPos, to: endPos });
                } catch (e) {
                  // If Tiptap position conversion fails, browser selection is still set
                  console.warn('Could not sync Tiptap selection:', e);
                }
              } catch (e) {
                console.warn('Error setting range:', e);
              }
            },
            
            // Undo/Redo - used by voice commands
            undo: () => {
              editor.commands.undo();
            },
            redo: () => {
              editor.commands.redo();
            },
            
            // Exec command - used by voice commands
            execCommand: (command) => {
              const commandMap = {
                'undo': () => editor.commands.undo(),
                'redo': () => editor.commands.redo(),
                'bold': () => editor.commands.toggleBold(),
                'italic': () => editor.commands.toggleItalic(),
                'underline': () => editor.commands.toggleUnderline(),
              };
              
              if (commandMap[command]) {
                commandMap[command]();
                return true;
              }
              return false;
            },
            
            // Scroll management - used by cursor position restoration
            saveScroll: () => {
              const container = editor.view.dom.closest('.relative') || 
                               editor.view.dom.parentElement;
              if (container) {
                scrollInfoRef.current = {
                  scrollTop: container.scrollTop || 0,
                  scrollLeft: container.scrollLeft || 0,
                };
                return scrollInfoRef.current;
              }
              return { scrollTop: 0, scrollLeft: 0 };
            },
            
            restoreScroll: (scrollInfo) => {
              if (scrollInfo) {
                const container = editor.view.dom.closest('.relative') || 
                                 editor.view.dom.parentElement;
                if (container) {
                  container.scrollTop = scrollInfo.scrollTop || 0;
                  container.scrollLeft = scrollInfo.scrollLeft || 0;
                }
              }
            },
            
            // Context element - CRITICAL for hooks to access editor DOM
            context: {
              element: {
                wysiwyg: wysiwygElement, // Used extensively by hooks
                codeView: wysiwygElement, // Tiptap doesn't have separate code view
              },
            },
          },
          
          // Direct setRange method - used as fallback
          setRange: (range) => {
            if (range) {
              try {
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
                
                // Try to sync with Tiptap
                try {
                  const startPos = editor.view.posAtDOM(range.startContainer, range.startOffset);
                  const endPos = editor.view.posAtDOM(range.endContainer, range.endOffset);
                  editor.commands.setTextSelection({ from: startPos, to: endPos });
                } catch (e) {
                  // Browser selection is set even if Tiptap sync fails
                }
              } catch (e) {
                console.warn('Error in setRange:', e);
              }
            }
          },
        },
      };
    }
  }, [editor, editorRef, language]);

  if (!editor) {
    return null;
  }

  return (
    <div className="relative" style={{ overflow: 'visible' }}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
        >
          {label}
        </label>
      )}
      <div
        className={`relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm ${
          isDarkMode ? 'dark' : ''
        }`}
        style={{ overflow: 'visible' }}
      >
        {/* Toolbar */}
        <div
          data-rte-toolbar-sticky="true"
          className="sticky top-16 z-10 bg-white dark:bg-gray-800"
        >
          <RTEToolbar editor={editor} handleImageUpload={handleImageUpload} />
        </div>

        {/* Editor Content - full width so drop works in empty space to the right of short lines */}
        <div className="overflow-auto bg-white dark:bg-gray-800 rte-editor-scroll" style={{ maxHeight: `${heightValue}px` }}>
          <EditorContent editor={editor} />
        </div>
      </div>

      {/* Image Annotation Modal */}
      {imageToAnnotate && (
        <ImageAnnotationModal
          imageFile={imageToAnnotate}
          onSave={handleAnnotatedImageSave}
          onCancel={handleAnnotationCancel}
          isUploading={isUploadingImage}
        />
      )}
    </div>
  );
}

export default RTE;
